import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../Api/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  
  // Prevent double API calls
  const hasVerified = useRef(false);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const verifyPayment = async () => {
      // Prevent double execution
      if (hasVerified.current) return;
      
      if (!sessionId || !orderId) {
        setError('Missing payment session information');
        setVerificationStatus('error');
        return;
      }

      hasVerified.current = true;

      try {
        console.log('Verifying payment...', { sessionId, orderId });
        
        // Verify payment with your backend
        const response = await api.get(`users/verify-payment/${sessionId}`);
        
        console.log('Verification response:', response.data);
        
        if (response.data.success) {
          setOrderDetails({
            orderno: response.data.orderno,
            amount: response.data.amount_total,
            status: response.data.payment_status,
            transactionid: response.data.transactionid
          });
          setVerificationStatus('success');
        } else {
          setError(response.data.error || 'Payment verification failed');
          setVerificationStatus('failed');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        
        // Check if it's a 400 error with "already processed" message
        if (err.response?.status === 400 && 
            err.response?.data?.error?.includes('already processed')) {
          // Payment was already processed successfully
          setOrderDetails({
            orderno: orderId,
            amount: 'Completed',
            status: 'paid',
            transactionid: 'Processed'
          });
          setVerificationStatus('success');
        } else {
          setError(err.response?.data?.error || 'Unable to verify payment status');
          setVerificationStatus('error');
        }
      }
    };

    verifyPayment();
  }, [sessionId, orderId]); // Dependencies are stable

  const handleContinueShopping = () => {
    navigate('/');
  };

  const handleViewOrders = () => {
    navigate('/my-orders');
  };

  if (verificationStatus === 'verifying') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Verifying payment...</span>
            </div>
            <h3 className="mt-3">Verifying your payment...</h3>
            <p>Please wait while we confirm your payment details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error' || verificationStatus === 'failed') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 text-center">
            <div className="alert alert-danger">
              <h2>Payment Verification Issue</h2>
              <p>{error || 'There was an issue verifying your payment.'}</p>
              <p>Order ID: <strong>{orderId}</strong></p>
              <p>Your payment may have been processed successfully. Please check your email for confirmation or contact support.</p>
            </div>
            <div className="mt-4">
              <button 
                className="btn btn-primary me-3"
                onClick={handleContinueShopping}
              >
                Continue Shopping
              </button>
              <button 
                className="btn btn-outline-primary"
                onClick={() => window.location.href = 'mailto:support@yourdomain.com'}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <div 
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#28a745',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontSize: '40px',
                    color: 'white'
                  }}
                >
                  ✓
                </div>
              </div>
              
              <h1 className="text-success mb-3">Payment Successful!</h1>
              <p className="lead mb-4">
                Thank you for your purchase. Your payment has been processed successfully.
              </p>

              {orderDetails && (
                <div className="bg-light p-4 rounded mb-4">
                  <h5>Order Details</h5>
                  <div className="row">
                    <div className="col-sm-6">
                      <strong>Order Number:</strong>
                      <br />
                      <span className="text-primary">{orderDetails.orderno}</span>
                    </div>
                    <div className="col-sm-6">
                      <strong>Amount Paid:</strong>
                      <br />
                      <span className="text-success">
                        {typeof orderDetails.amount === 'number' 
                          ? `£${orderDetails.amount.toFixed(2)}`
                          : orderDetails.amount}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="alert alert-info">
                <h6>What's Next?</h6>
                <ul className="list-unstyled mb-0 text-start">
                  <li>• You will receive an email confirmation shortly</li>
                  <li>• Your order is being prepared for collection/delivery</li>
                  <li>• You can track your order status in your account</li>
                </ul>
              </div>

              <div className="mt-4">
                <button 
                  className="btn btn-success btn-lg me-3"
                  onClick={handleViewOrders}
                >
                  View My Orders
                </button>
                <button 
                  className="btn btn-outline-primary btn-lg"
                  onClick={handleContinueShopping}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;